// 9fbef606107a605d69c0edbcd8029e5d
import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import Anchor from '@tcp/core/src/components/common/atoms/Anchor';
import BodyCopy from '@tcp/core/src/components/common/atoms/BodyCopy';
import Heading from '@tcp/core/src/components/common/atoms/Heading';
import TextItems from '@tcp/core/src/components/common/atoms/TextItems';
import LinkTextWrapper from '../LinkText.style';
import { configureInternalNavigationFromCMSUrl } from '../../../../../utils';

const renderTextItems = (
  noLinkWrap,
  Component,
  heading,
  compProps,
  {
    textItems,
    icon,
    titleStyle,
    headerTextStyles,
    birthdayProps,
    renderInNewLine,
    additionalClassName,
  }
) => {
  return (
    <Component
      {...compProps}
      className={`${heading} ${noLinkWrap ? 'no-linked-wrap' : 'link-text'} ${additionalClassName}`}
    >
      <TextItems
        textItems={textItems}
        icon={icon}
        titleStyle={titleStyle}
        headerTextStyles={headerTextStyles}
        birthdayProps={birthdayProps}
        renderInNewLine={renderInNewLine}
      />
    </Component>
  );
};

const renderLinkComp = (
  index,
  noLinkWrap,
  Component,
  textItems,
  icon,
  titleStyle,
  {
    headerTextStyles,
    heading,
    compProps,
    navigationUrl,
    birthdayProps,
    renderInNewLine,
    additionalClassName,
    moduleAnalyticsTag,
    useRouterNavigation,
  }
) => {
  return noLinkWrap ? (
    renderTextItems(noLinkWrap, Component, heading, compProps, {
      textItems,
      icon,
      titleStyle,
      headerTextStyles,
      birthdayProps,
      renderInNewLine,
      additionalClassName,
    })
  ) : (
    <Anchor
      key={index.toString()}
      moduleAnalyticsTag={moduleAnalyticsTag}
      {...navigationUrl}
      useRouterNavigation={useRouterNavigation}
    >
      {renderTextItems(noLinkWrap, Component, heading, compProps, {
        textItems,
        icon,
        titleStyle,
        headerTextStyles,
        birthdayProps,
        renderInNewLine,
        additionalClassName,
      })}
    </Anchor>
  );
};

const getTitleStyle = (headerStyle, index) =>
  Array.isArray(headerStyle) ? headerStyle[index] : {};

const getNavigationUrl = (link) => link || {};

/**
 * This component creates a link with styled text
 * Text can be configured to be inside a heading tag to some other element.
 * This component uses BodyCopy atom and Heading atom and differentiates based on type="heading"
 * @param {*} props
 * type="heading" if heading is required
 * component="div|any" if type is heading then component should be "h1|h2|h3|h4|h5|h6"
 * accepts all parameters for BodyCopy and Heading atom
 *
 * [TODO] Can configure icon in heading at start|middle|last
 */
const LinkText = (props) => {
  const {
    className,
    type,
    component,
    variant,
    headerText,
    headingClass,
    color,
    dataLocator,
    headerStyle,
    headerTextStyles,
    overrideStyle,
    noLinkWrap,
    promo,
    inheritedStyles,
    birthdayProps,
    renderInNewLine,
    additionalClassName,
    moduleAnalyticsTag,
    useRouterNavigation,
    ...otherProps
  } = props;
  let Component;
  let compProps = {};
  const heading = headingClass || '';

  if (type === 'heading') {
    Component = Heading;
    compProps.component = component || variant;
    compProps.variant = variant || component;
    compProps.color = color;
    compProps.dataLocator = dataLocator;
    compProps.color = color;
  } else {
    Component = BodyCopy;
    compProps = {
      component,
      'data-locator': dataLocator,
      ...otherProps,
    };
  }

  return (
    <LinkTextWrapper
      promo={promo}
      inheritedStyles={inheritedStyles}
      className={className}
      style={overrideStyle}
      headerText={headerText}
    >
      {Array.isArray(headerText) &&
        headerText.map((item, index) => {
          const { link, icon, textItems } = item;
          const titleStyle = getTitleStyle(headerStyle, index);
          const navigationUrl = Object.assign({}, getNavigationUrl(link));
          if (!isEmpty(navigationUrl)) {
            navigationUrl.to = link && configureInternalNavigationFromCMSUrl(link.url);
            navigationUrl.asPath = link && link.url;
            navigationUrl.target = link && link.target;
            navigationUrl.title = link && link?.title;
          }

          if (type === 'heading') {
            compProps.dataLocator = `${compProps.dataLocator}_${index}`;
          } else {
            compProps['data-locator'] = `${compProps['data-locator']}_${index}`;
          }

          return renderLinkComp(index, noLinkWrap, Component, textItems, icon, titleStyle, {
            headerTextStyles,
            heading,
            compProps,
            navigationUrl,
            birthdayProps,
            renderInNewLine,
            additionalClassName,
            moduleAnalyticsTag,
            useRouterNavigation,
          });
        })}
    </LinkTextWrapper>
  );
};

LinkText.defaultProps = {
  type: '',
  component: () => null,
  headerText: [],
  className: '',
  dataLocator: '',
  headingClass: '',
  color: '',
  variant: '',
  headerStyle: '',
  headerTextStyles: '',
  noLinkWrap: false,
  additionalClassName: '',
  useRouterNavigation: false,
};

LinkText.propTypes = {
  type: PropTypes.string,
  component: PropTypes.elementType,
  headerText: PropTypes.arrayOf(PropTypes.shape({})),
  className: PropTypes.string,
  dataLocator: PropTypes.string,
  headingClass: PropTypes.string,
  color: PropTypes.string,
  variant: PropTypes.string,
  headerStyle: PropTypes.string,
  headerTextStyles: PropTypes.string,
  noLinkWrap: PropTypes.bool,
  additionalClassName: PropTypes.string,
  useRouterNavigation: PropTypes.bool,
};

export default LinkText;
export { LinkText as VanillaLinkText };
